<template>
  <div>
    <UserCenterLayout
      :side-menus="sideMenus"
      :active-menu-key="activeMenuKey"
      @side-menu-click="handleSideMenu"
    >
      <section ref="profile" class="profile-hero">
        <div class="avatar-placeholder"></div>
        <div class="profile-info">
          <div class="profile-name-row">
            <h1>星核用户</h1>
            <span class="verified-badge">已认证</span>
          </div>
          <div class="mobile-row">
            {{ maskedMobile }}
            <button class="copy-mobile" type="button" aria-label="复制手机号" @click="copyMobile">
              <img :src="userIcons.copy" alt="">
            </button>
          </div>
        </div>
        <button class="recharge-button" type="button" @click="goRecharge">立即充值</button>
      </section>

      <section class="balance-overview" aria-label="用户余额">
        <div class="balance-primary">
          <small>总可用额度</small>
          <strong>{{ formatBalance(balance.total) }}</strong>
        </div>
        <div>
          <small>基础充值额度</small>
          <strong>{{ formatBalance(balance.base) }}</strong>
        </div>
        <div>
          <small>赠送额度</small>
          <strong>{{ formatBalance(balance.bonus) }}</strong>
        </div>
        <div>
          <small>即将过期赠送额度</small>
          <strong>{{ formatBalance(balance.expiringBonus) }}</strong>
          <em v-if="balanceExpirationText">{{ balanceExpirationText }} 到期</em>
        </div>
        <p v-if="balanceLoading" class="balance-state">余额加载中...</p>
        <p v-else-if="balanceError" class="balance-state balance-error">
          {{ balanceError }}
          <button type="button" @click="loadWallet">重新加载</button>
        </p>
      </section>

      <section ref="plan" class="content-card plan-card">
        <h2>当前套餐</h2>
        <div class="plan-content">
          <div class="current-plan" :class="{ 'no-plan': !hasActiveEntitlement }">
            <div class="plan-top">
              <img class="plan-icon" :src="userIcons.plan" alt="">
              <div>
                <div class="plan-title">{{ hasActiveEntitlement ? '趋势中枢版' : '暂无开通套餐' }}</div>
                <p v-if="hasActiveEntitlement">适合希望把趋势判断嵌入日常经营的品牌团队</p>
              </div>
            </div>
            <div v-if="hasActiveEntitlement" class="plan-meta">
              <div>
                <span>有效期至</span>
                <strong>{{ planExpirationText }}</strong>
              </div>
              <div>
                <span>剩余时间</span>
                <strong>{{ planRemainingText }}</strong>
              </div>
            </div>
          </div>
          <div class="plan-actions">
            <button
              v-if="!hasActiveEntitlement"
              class="primary-action"
              type="button"
              @click="goPurchase"
            >开通套餐</button>
            <template v-else>
              <button class="primary-action" type="button" @click="goRenew">立即续费</button>
              <button class="secondary-action" type="button" @click="goUpgrade">升级套餐</button>
            </template>
          </div>
        </div>
        <p class="expire-tip" v-if="false">
          <img :src="userIcons.tip" alt="">
          到期前 3 天系统将通过邮件和短信提醒续费。
        </p>
      </section>

      <section ref="status" class="content-card">
        <h2>多端使用状态</h2>
        <div class="status-grid">
          <article v-for="item in terminalStatus" :key="item.key" class="terminal-card">
            <img class="terminal-icon" :src="item.icon" alt="">
            <div class="terminal-content">
              <div class="terminal-title-row">
                <strong>{{ item.title }}</strong>
                <span :class="{ muted: !item.opened }">{{ item.status }}</span>
              </div>
              <p>{{ item.desc }}</p>
            </div>
          </article>
        </div>
      </section>

      <section ref="invoice" class="content-card">
        <p v-if="isInvoiceDisabled" class="invoice-trial-tip">{{ invoiceDisabledTip }}</p>
        <h2>发票管理</h2>
        <div class="invoice-grid">
          <button
            v-for="item in invoiceActions"
            :key="item.key"
            class="invoice-action"
            :class="{ disabled: isInvoiceDisabled }"
            type="button"
            :disabled="isInvoiceDisabled"
            @click="handleInvoiceAction(item)"
          >
            <img class="invoice-icon" :src="item.icon" alt="">
            <span>
              <strong>{{ item.title }}</strong>
              <em>{{ item.desc }}</em>
            </span>
            <img class="invoice-arrow" :src="userIcons.right" alt="">
          </button>
        </div>
      </section>

      <section ref="orders" class="content-card order-history-card">
        <h2>历史订单</h2>
        <div class="order-table-wrap">
          <table class="order-table">
            <thead>
              <tr>
                <th>套餐名称</th>
                <th>金额</th>
                <th>支付方式</th>
                <th>状态</th>
                <th>购买时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in orderHistory" :key="item.key">
                <td>{{ item.planName }}</td>
                <td>{{ item.amount }}</td>
                <td>
                  <span class="payment-method">
                    <img :src="item.paymentIcon" alt="">
                    {{ item.paymentMethod }}
                  </span>
                </td>
                <td>
                  <span class="order-status" :class="item.statusType">{{ item.status }}</span>
                </td>
                <td>{{ item.paidAt }}</td>
              </tr>
              <tr v-if="!orderLoading && orderHistory.length === 0">
                <td colspan="5">暂无订单</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="content-card service-card">
        <h2>专属客服</h2>
        <div class="service-grid">
          <article class="service-item wechat-service">
            <div class="service-info">
              <div class="service-large-icon-box qr">
                <img class="service-large-icon" :src="userIcons.wechatQr" alt="">
              </div>
              <div class="service-copy">
                <strong>添加企业微信</strong>
                <p>专属客服一对一服务</p>
              </div>
            </div>
            <button class="service-button" type="button" @click="goEnterpriseWechat">
              <img :src="userIcons.wechatBusiness" alt="">
              添加企业微信
            </button>
          </article>
        </div>
      </section>
    </UserCenterLayout>

  </div>
</template>

<script>
import auth from '@/store/auth'
import userCenter from '@/store/userCenter'
import { hasValidTrial } from '@/store/trial'
import { platform } from '@/services/platform'
import { mapWalletBalance } from '@/services/recharge'
import UserCenterLayout from '@/layouts/UserCenterLayout.vue'
import businessWechatLineIcon from '@/assets/images/user/wechat_line.svg'
import onlineCustomerIcon from '@/assets/images/user/zxkf.svg'
import copyIcon from '@/assets/images/user/copy.svg'
import copyBlueIcon from '@/assets/images/user/copy_blue.svg'
import planIcon from '@/assets/images/user/taocanicon.svg'
import tipIcon from '@/assets/images/user/tip.svg'
import webIcon from '@/assets/images/user/wyd.svg'
import miniproIcon from '@/assets/images/user/xcxd.svg'
import invoiceIcon from '@/assets/images/user/sqfp.svg'
import invoiceRecordIcon from '@/assets/images/user/fpjl.svg'
import rightIcon from '@/assets/images/user/right.svg'
import wechatGreenIcon from '@/assets/images/icons/wechat-green.svg'
import alipayIcon from '@/assets/images/icons/zfb.svg'
import qrPng from '@/assets/images/subpage/enterprise-wechat-qr.png'
import wechatBusiness from '@/assets/images/subpage/wechatBusiness.svg'

// 常量：未登录或本地缓存缺失时的手机号展示兜底
const DEFAULT_MASKED_MOBILE = '138****0754'

// 常量：钱包尚未加载时使用零值模型，避免把原型数据展示为真实余额
const DEFAULT_BALANCE = {
  total: 0,
  base: 0,
  bonus: 0,
  expiringBonus: 0,
  expiringAt: null
}

// 常量：个人中心使用的 user 目录图标入口
const USER_ICONS = {
  businessWechatLine: businessWechatLineIcon,
  wechatQr: qrPng,
  onlineCustomer: onlineCustomerIcon,
  copy: copyIcon,
  copyBlue: copyBlueIcon,
  wechatBusiness: wechatBusiness,
  plan: planIcon,
  tip: tipIcon,
  right: rightIcon
}

// 常量：左侧导航统一配置内容锚点与独立页面路由入口
const SIDE_MENUS = [
  { key: 'profile', label: '用户信息', target: 'profile' },
  // 关键节点：充值中心没有个人中心内的锚点，使用路由名称触发页面跳转
  { key: 'recharge', label: '充值中心', routeName: 'recharge-center' },
  { key: 'plan', label: '当前套餐', target: 'plan' },
  { key: 'status', label: '多端状态', target: 'status' },
  { key: 'invoice', label: '发票管理', target: 'invoice' },
  { key: 'orders', label: '订单记录', target: 'orders' }
]

// 常量：多端状态的基础展示配置，开通状态由权益接口结果统一决定
const TERMINAL_STATUS = [
  {
    key: 'web',
    title: '网页端',
    icon: webIcon
  },
  {
    key: 'minipro',
    title: '小程序端',
    icon: miniproIcon
  }
]

// 常量：发票管理入口，申请发票在全局抽屉内切换视图
const INVOICE_ACTIONS = [
  { key: 'apply', title: '申请发票', desc: '申请电子发票', icon: invoiceIcon, action: 'open-invoice' },
  { key: 'record', title: '发票记录', desc: '查看历史发票', icon: invoiceRecordIcon, tip: '发票记录功能开发中，待接入' }
]

export default {
  name: 'UserCenterView',
  components: { UserCenterLayout },
  props: {
    // 常量参数：抽屉打开时需要定位的个人中心模块
    initialSection: {
      type: String,
      default: 'profile'
    }
  },
  data() {
    return {
      authState: auth.state,
      activeMenuKey: this.initialSection || 'profile',
      userIcons: USER_ICONS,
      sideMenus: SIDE_MENUS,
      invoiceActions: INVOICE_ACTIONS,
      orderHistory: [],
      orderLoading: false,
      balance: { ...DEFAULT_BALANCE },
      balanceLoading: false,
      balanceError: '',
      isTrialUser: false,
      // 状态：接口异常时保留原有已开通展示，避免网络错误被误判为未开通套餐
      hasActiveEntitlement: true,
      // 状态：null 表示接口日期未知，空字符串表示存在长期有效权益
      latestEntitlementExpiresAt: null
    }
  },
  computed: {
    // 方法：把登录手机号脱敏成个人中心展示格式
    maskedMobile() {
      return this.maskMobile(this.authState.user && this.authState.user.mobile)
    },
    // 方法：根据权益状态生成网页端和小程序端的统一展示数据
    terminalStatus() {
      const opened = this.hasActiveEntitlement
      return TERMINAL_STATUS.map(item => ({
        ...item,
        opened,
        status: opened ? '已开通' : '未开通',
        desc: opened
          ? (item.key === 'web' ? '当前账号已在网页端开通使用' : '绑定后即可在小程序端同步使用')
          : '当前账号暂未开通套餐'
      }))
    },
    // 方法：试用用户和未开通套餐用户均不可使用发票管理
    isInvoiceDisabled() {
      return this.isTrialUser || !this.hasActiveEntitlement
    },
    // 方法：根据禁用原因显示发票管理提示，试用提示优先于套餐提示
    invoiceDisabledTip() {
      return this.isTrialUser ? '试用套餐无法开具发票' : '未开通套餐，暂无法使用发票管理'
    },
    // 方法：将即将过期时间转换为本地日期，缺失时不展示到期提示
    balanceExpirationText() {
      if (!this.balance.expiringAt) return ''
      const date = new Date(this.balance.expiringAt)
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('zh-CN')
    },
    // 方法：把最晚权益时间格式化为当前时区的 YYYY-MM-DD
    planExpirationText() {
      if (this.latestEntitlementExpiresAt === null) return '--'
      if (!this.latestEntitlementExpiresAt) return '长期有效'
      return this.formatDate(this.latestEntitlementExpiresAt)
    },
    // 方法：按最晚权益时间计算剩余自然时长，未满一天按一天展示
    planRemainingText() {
      if (this.latestEntitlementExpiresAt === null) return '--'
      if (!this.latestEntitlementExpiresAt) return '长期有效'
      const expiresTime = Date.parse(this.latestEntitlementExpiresAt)
      if (!Number.isFinite(expiresTime)) return '--'
      const remainingDays = Math.max(Math.ceil((expiresTime - Date.now()) / (24 * 60 * 60 * 1000)), 0)
      return `${remainingDays} 天`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.syncSection(this.initialSection)
    })

    // 逻辑分支：未登录打开抽屉时，先唤起登录弹窗，登录后继续加载当前视图
    if (!auth.isLoggedIn()) {
      auth.openLoginDialog(() => {
        this.loadOrders()
        this.loadEntitlementStatus()
        this.loadWallet()
      })
    } else {
      this.loadOrders()
      this.loadEntitlementStatus()
      this.loadWallet()
    }
  },
  watch: {
    // 关键节点：抽屉从发票视图切回概览时同步新的模块定位
    initialSection(section) {
      this.$nextTick(() => {
        this.syncSection(section)
      })
    }
  },
  methods: {
    // 方法：加载当前账号钱包余额，失败时展示独立重试入口
    async loadWallet() {
      if (!auth.isLoggedIn()) return
      this.balanceLoading = true
      this.balanceError = ''
      try {
        this.balance = mapWalletBalance(await platform.wallet())
      } catch (error) {
        this.balance = { ...DEFAULT_BALANCE }
        this.balanceError = error.message || '账户余额加载失败，请稍后重试'
      } finally {
        this.balanceLoading = false
      }
    },
    // 方法：统一加载权益并同步套餐、多端状态和发票可用性
    async loadEntitlementStatus() {
      try {
        const entitlements = await platform.entitlements()
        const activeEntitlements = this.normalizeEntitlements(entitlements)
          .filter(entitlement => this.isEntitlementActive(entitlement))
        const hasSubscription = activeEntitlements.some(
          entitlement => this.isActiveSubscriptionEntitlement(entitlement)
        )
        const hasTrial = activeEntitlements.some(
          entitlement => this.isActiveTrialEntitlement(entitlement)
        )

        // 逻辑分支：接口成功但不存在有效权益时，个人中心进入未开通套餐状态
        this.hasActiveEntitlement = activeEntitlements.length > 0
        this.latestEntitlementExpiresAt = this.getLatestEntitlementExpiresAt(activeEntitlements)

        // 逻辑分支：正式订阅用户即便有历史试用权益，也允许继续使用发票功能
        this.isTrialUser = hasTrial && !hasSubscription
      } catch (error) {
        // 逻辑分支：权益接口异常时不切换为空套餐，仅降级读取本地试用记录判断发票状态
        this.isTrialUser = hasValidTrial()
      }
    },
    // 方法：兼容数组与 items 响应结构，统一转换为权益列表
    normalizeEntitlements(entitlements) {
      return Array.isArray(entitlements)
        ? entitlements
        : (entitlements && Array.isArray(entitlements.items) ? entitlements.items : [])
    },
    // 方法：从当前有效权益中提取最晚的合法 expires_at 时间
    getLatestEntitlementExpiresAt(entitlements) {
      if (entitlements.length === 0) return null
      // 逻辑分支：任一有效权益未设置过期时间时，整体有效期视为长期有效。
      if (entitlements.some(entitlement => !entitlement.expires_at)) return ''
      const latest = entitlements.reduce((current, entitlement) => {
        const expiresTime = Date.parse(entitlement && entitlement.expires_at)
        if (!Number.isFinite(expiresTime)) return current
        return !current || expiresTime > current.time
          ? { time: expiresTime, value: entitlement.expires_at }
          : current
      }, null)
      return latest ? latest.value : null
    },
    // 方法：判断权益来源是否包含仍在有效期内的试用权益
    isActiveTrialEntitlement(entitlement) {
      return this.hasEntitlementSource(entitlement, 'trial') && this.isEntitlementActive(entitlement)
    },
    // 方法：判断权益来源是否包含仍在有效期内的正式订阅权益
    isActiveSubscriptionEntitlement(entitlement) {
      return this.hasEntitlementSource(entitlement, 'subscription') && this.isEntitlementActive(entitlement)
    },
    // 方法：按接口 sources 字段判断指定权益来源
    hasEntitlementSource(entitlement, source) {
      return Boolean(
        entitlement &&
        Array.isArray(entitlement.sources) &&
        entitlement.sources.includes(source)
      )
    },
    // 方法：判断权益是否仍有效，未返回过期时间时按有效权益处理
    isEntitlementActive(entitlement) {
      if (!entitlement || !entitlement.expires_at) {
        return true
      }

      return Date.parse(entitlement.expires_at) > Date.now()
    },
    async loadOrders() {
      this.orderLoading = true
      try {
        const orders = await platform.orders()
        this.orderHistory = orders.map(order => {
          const provider = order.payment_provider
          const succeeded = order.payment_status === 'SUCCEEDED'
          const failed = ['FAILED', 'TIMEOUT'].includes(order.payment_status)
          const status = succeeded
            ? '支付成功'
            : (failed ? '支付失败' : '待支付')
          return {
            key: order.id,
            planName: `${this.planName(order.plan_sku)}（${this.periodName(order.period)}）`,
            amount: `¥${(order.amount_cents / 100).toFixed(2)}`,
            paymentMethod: provider === 'wechat' ? '微信支付' : (provider === 'alipay' ? '支付宝支付' : '测试支付'),
            paymentIcon: provider === 'wechat' ? wechatGreenIcon : alipayIcon,
            status,
            statusType: succeeded ? 'success' : (failed ? 'failed' : ''),
            paidAt: order.paid_at ? new Date(order.paid_at).toLocaleString('zh-CN', { hour12: false }) : '-'
          }
        })
      } catch (error) {
        this.handlePlaceholder(error.message || '订单加载失败')
      } finally {
        this.orderLoading = false
      }
    },
    planName(sku) {
      return {
        web: '网页端套餐',
        mini: '小程序套餐',
        both: '双端套餐'
      }[sku] || sku
    },
    periodName(period) {
      return {
        month: '月付',
        quarter: '季付',
        year: '年付'
      }[period] || period
    },
    // 方法：手机号展示脱敏，异常输入回落到默认示例
    maskMobile(mobile) {
      if (!mobile || String(mobile).length < 7) {
        return DEFAULT_MASKED_MOBILE
      }

      const mobileText = String(mobile)
      return `${mobileText.slice(0, 3)}****${mobileText.slice(-4)}`
    },
    // 方法：余额金额统一保留两位小数，保证卡片各列数值对齐
    formatBalance(value) {
      return Number(value || 0).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    // 方法：按本地时区格式化日期，避免浏览器默认格式不一致
    formatDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '--'
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    // 方法：左侧菜单滚动到对应模块，并同步当前菜单高亮
    handleSideMenu(item) {
      // 逻辑分支：路由菜单项直接离开个人中心抽屉，不参与内容区滚动
      if (item.routeName) {
        userCenter.close()
        this.$router.push({ name: item.routeName, query: { source: 'account' } })
        return
      }

      const target = this.$refs[item.target]
      if (!target) {
        return
      }

      this.activeMenuKey = item.key
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    // 方法：根据抽屉 section 参数滚动到指定个人中心模块
    syncSection(section) {
      const matchedMenu = this.sideMenus.find(item => item.key === section)
      if (!matchedMenu) {
        return
      }

      const target = this.$refs[matchedMenu.target]
      if (!target) {
        return
      }

      this.activeMenuKey = matchedMenu.key
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    // 方法：进入企业微信客服承接页
    goEnterpriseWechat() {
      userCenter.close()
      this.$router.push('/enterprise-wechat')
    },
    // 方法：续费默认进入双端季付购买页
    goRenew() {
      userCenter.close()
      this.$router.push({ path: '/payment-order', query: { mode: 'both', period: 'quarter' } })
    },
    // 方法：升级套餐默认进入双端年付购买页
    goUpgrade() {
      userCenter.close()
      this.$router.push({ path: '/payment-order', query: { mode: 'both', period: 'year' } })
    },
    // 方法：未开通套餐时进入默认的双端季付购买页
    goPurchase() {
      userCenter.close()
      this.$router.push({ path: '/payment-order', query: { mode: 'both', period: 'quarter' } })
    },
    // 方法：从个人中心进入自由充值模式，先关闭抽屉避免遮挡充值页。
    goRecharge() {
      userCenter.close()
      this.$router.push({ name: 'recharge-center', query: { source: 'account' } })
    },
    // 关键节点：多端状态按钮只处理当前已有的绑定小程序占位
    handleTerminalAction(item) {
      if (item.key === 'minipro') {
        this.handlePlaceholder('小程序绑定功能开发中，待接入')
      }
    },
    // 方法：发票管理入口分发，申请发票切换到抽屉内视图
    handleInvoiceAction(item) {
      // 逻辑分支：试用账号或未开通套餐账号不允许使用发票管理，入口保持灰态不可用
      if (this.isInvoiceDisabled) {
        this.handlePlaceholder(this.invoiceDisabledTip)
        return
      }

      if (item.action === 'open-invoice') {
        this.$emit('open-invoice')
        return
      }

      this.handlePlaceholder(item.tip)
    },
    // 方法：复制当前登录手机号，浏览器不支持剪贴板时静默降级
    copyMobile() {
      const mobile = this.authState.user && this.authState.user.mobile
      if (navigator.clipboard && mobile) {
        navigator.clipboard.writeText(mobile)
      }
      this.handlePlaceholder('已复制手机号')
    },
    // 方法：展示轻量占位反馈
    handlePlaceholder(message) {
      this.$toast(message || '功能开发中，待接入')
    }
  }
}
</script>

<style scoped lang="less">
.profile-hero{
  height: 135px;
  padding: 0 35px;
  border-radius: 8px;
  background: linear-gradient(121deg, #93BAFF 0%, #E2E4FF 100%);
  display: flex;
  align-items: center;
}

.avatar-placeholder{
  width: 70px;
  height: 70px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #D8D8D8;
  box-shadow: 0 6px 14px rgba(64, 121, 255, 0.12);
}

.profile-info{
  margin-left: 25px;
}
.recharge-button{
  min-width: 128px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(100deg, #3286f5, #615ce8);
  font-size: 12px;
  font-weight: 750;
  margin-left: auto;
}

.balance-overview{
  min-height: 116px;
  margin-top: 14px;
  border: 1px solid #DCE4F0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  display: grid;
  grid-template-columns: 1.15fr repeat(3, 1fr);
}

.balance-overview > div{
  min-width: 0;
  padding: 14px 13px;
  border-right: 1px solid #E5EAF1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
}

.balance-overview > div:nth-of-type(4){
  border-right: 0;
}

.balance-overview small{
  overflow: hidden;
  color: #7A8799;
  font-size: 10px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.balance-overview strong{
  margin-top: 7px;
  color: #263A57;
  font-size: 21px;
  line-height: 27px;
  font-weight: 600;
}

.balance-overview em{
  margin-top: 3px;
  color: #BA6A3F;
  font-size: 9px;
  line-height: 14px;
  font-style: normal;
}

.balance-state{
  grid-column: 1 / -1;
  margin: 0;
  padding: 9px 24px;
  border-top: 1px solid #E6EAF0;
  background: #FAFBFD;
  color: #7A8799;
  font-size: 12px;
  line-height: 20px;
}

.balance-error{
  color: #B34B4B;
}

.balance-state button{
  margin-left: 8px;
  border: 0;
  background: transparent;
  color: #2F7BFF;
  cursor: pointer;
}

.balance-primary{
  background: linear-gradient(110deg, #3A80F5 0%, #5B5BE6 100%);
}

.balance-primary small,
.balance-primary strong{
  color: #fff;
}

.balance-primary strong{
  font-size: 25px;
}

.profile-name-row{
  display: flex;
  align-items: center;
  h1{
    margin: 0;
    color: #203A5F;
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
  }
}

.verified-badge{
  height: 18px;
  margin-left: 10px;
  padding: 0 8px;
  border-radius: 4px;
  background: #fff;
  color: #2F7BFF;
  font-size: 12px;
  line-height: 18px;
}

.mobile-row{
  margin-top: 7px;
  color: #203A5F;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
}

.copy-mobile{
  width: 18px;
  height: 18px;
  margin-left: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  img{
    width: 12px;
    height: 12px;
    display: block;
  }
}

.content-card{
  margin-top: 22px;
  padding: 18px 30px 24px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(31, 65, 129, 0.07);

  h2{
    margin: 0;
    color: #000000D9;
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
}

.plan-card{
  padding-bottom: 18px;
}

.plan-content{
  margin-top: 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.current-plan{
  width: 355px;
  height: 146px;
  border-radius: 6px;
  overflow: hidden;
  background: linear-gradient(130deg, #7BDDF8 0%, #75A3FF 100%);
}

.current-plan.no-plan{
  display: flex;
  align-items: center;
  height: 92px;
}

.current-plan.no-plan .plan-top{
  height: auto;
}

.plan-top{
  height: 79px;
  padding: 0 30px;
  display: flex;
  align-items: center;
  color: #fff;
}

.plan-icon{
  width: 28px;
  height: 28px;
  margin-right: 20px;
}

.plan-title{
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
}

.plan-top p{
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 18px;
}

.plan-meta{
  height: 67px;
  padding: 0 30px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: rgba(201, 226, 255, 0.82);
  color: #203A5F;

  div{
    padding-top: 12px;
  }

  div + div{
    padding-left: 20px;
    border-left: 1px solid rgba(255, 255, 255, 0.92);
  }

  span{
    display: block;
    color: #53677F;
    font-size: 12px;
    line-height: 18px;
  }

  strong{
    display: block;
    margin-top: 4px;
    color: #203A5F;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
  }
}

.plan-actions{
  width: 113px;
}

.primary-action,
.secondary-action{
  margin-top: 20px;
  width: 113px;
  height: 36px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.primary-action{
  border: none;
  background: #EDF3FF;
  color: #333;
  font-weight: 500;
}

.secondary-action{
  border: 1px solid #D9D9D9;
  background: #fff;
  color: #666;
}

.expire-tip{
  margin: 8px 0 0;
  color: #00000073;
  font-size: 12px;
  line-height: 18px;
  display: flex;
  align-items: center;
  img{
    width: 12px;
    height: 12px;
    margin-right: 8px;
  }
}

.status-grid{
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.terminal-card{
  height: 99px;
  padding: 12px 16px;
  border-radius: 6px;
  background: #EAF2FF;
  display: flex;
  box-sizing: border-box;
}

.terminal-icon{
  width: 38px;
  height: 38px;
  margin-right: 14px;
}

.terminal-content{
  flex: 1;
}

.terminal-title-row{
  display: flex;
  align-items: center;

  strong{
    color: #2F7BFF;
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
  }

  span{
    height: 18px;
    margin-left: 8px;
    padding: 0 6px;
    border-radius: 4px;
    background: #fff;
    color: #2F7BFF;
    font-size: 10px;
    line-height: 18px;

    &.muted{
      color: #999;
    }
  }
}

.terminal-content p{
  margin: 4px 0 0;
  color: #333333A6;
  font-size: 10px;
  line-height: 20px;
}

.mini-action{
  width: 76px;
  height: 24px;
  margin-top: 8px;
  border: 1px solid #2F7BFF;
  border-radius: 6px;
  background: #fff;
  color: #2F7BFF;
  font-size: 12px;
  cursor: pointer;
}

.invoice-grid{
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.invoice-trial-tip{
  margin: -4px 0 8px;
  color: #666;
  font-size: 12px;
  line-height: 20px;
}

.invoice-action{
  height: 60px;
  padding: 0 18px;
  border: 1px solid #D9D9D9;
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  cursor: pointer;

  &.disabled{
    background: #FAFAFA;
    cursor: not-allowed;

    .invoice-icon,
    .invoice-arrow{
      opacity: 0.42;
      filter: grayscale(1);
    }

    strong,
    em{
      color: #999;
    }
  }

  .invoice-icon{
    width: 30px;
    height: 30px;
    margin-right: 18px;
  }

  span{
    flex: 1;
  }

  strong,
  em{
    display: block;
  }

  strong{
    color: #2F7BFF;
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
  }

  em{
    margin-top: 2px;
    color: #666;
    font-size: 12px;
    line-height: 18px;
  }

  .invoice-arrow{
    width: 12px;
    height: 12px;
    margin-left: 12px;
    object-fit: contain;
  }
}

.order-history-card{
  padding: 0;
  padding-bottom: 17px;
  h2 {
    padding: 18px 30px 0;
  }
}

.order-table-wrap{
  margin-top: 14px;
  overflow: hidden;
}

.order-table{
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  color: #333;
  font-size: 10px;
  line-height: 18px;

  th,
  td{
    height: 30px;
    padding: 0 6px;
    text-align: left;
    white-space: nowrap;
    box-sizing: border-box;
  }

  th{
    height: 25px;
    background: #F5F5F5;
    color: #333;
    font-size: 10px;
    font-weight: 500;
  }

  td{
    border-bottom: 1px solid #F2F2F2;
  }

  tbody tr:last-child td{
    border-bottom: none;
  }

  th:nth-child(1),
  td:nth-child(1){
    width: 25%;
  }

  th:nth-child(2),
  td:nth-child(2){
    width: 17%;
  }

  th:nth-child(3),
  td:nth-child(3){
    width: 23%;
  }

  th:nth-child(4),
  td:nth-child(4){
    width: 15%;
  }

  th:nth-child(5),
  td:nth-child(5){
    width: 20%;
  }
}

.payment-method{
  display: inline-flex;
  align-items: center;

  img{
    width: 12px;
    height: 12px;
    margin-right: 4px;
    object-fit: contain;
  }
}

.order-status{
  &.success{
    color: #2F7BFF;
  }

  &.failed{
    color: #FF4D4F;
  }
}

.service-card{
  margin-bottom: 0;
}

.service-grid{
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.service-item{
  height: 118px;
  padding: 6px;
  border-radius: 8px;
  background: #EAF2FF;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
}

.service-info{
  width: 100%;
  display: flex;
  align-items: center;
}

.qr-placeholder{
  width: 60px;
  height: 60px;
  margin-right: 16px;
  object-fit: contain;
}

.service-large-icon-box{
  width: 54px;
  height: 54px;
  margin-right: 18px;
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &.qr{
    img{
      width: 50px;
      height: 50px;
    }
  }
}
.service-large-icon{
  width: 38px;
  height: 35px;
}

.service-copy{
  flex: 1;
  strong{
    color: #2F7BFF;
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
  }
  p{
    margin: 4px 0 0;
    color: #333333A6;
    font-size: 12px;
    line-height: 20px;
  }
}

.service-button{
  width: 100%;
  height: 24px;
  border: 1px solid #4185FA;
  border-radius: 5px;
  background: #fff;
  color: #2E74FF;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  img{
    width: 14px;
    height: 14px;
    margin: 0 6px;
  }
  span{
    font-size: 10px;
  }
}

</style>
